import { Injectable } from '@angular/core';
import {
  getRemoteConfig,
  fetchAndActivate,
  getString,
  getBoolean,
  RemoteConfig,
} from 'firebase/remote-config';

import { environment } from '../../environments/environment';
import { getOrInitFirebaseApp } from './firebase-app.util';
import { EnvironmentDirectory, normalizeEnvironmentDirectory } from '../shared/environment-directory.util';

export { EnvironmentDirectoryEntry, EnvironmentDirectory } from '../shared/environment-directory.util';

const REMOTE_CONFIG_KEYS = {
  environmentDirectory: 'environment_directory',
  maintenanceMode: 'maintenance_mode',
  otpUrl: 'otp_url',
  minSupportedVersion: 'admin_dashboard_min_supported_version',
} as const;

const FALLBACK_DIRECTORY: EnvironmentDirectory = {
  dev: {
    baseUrl: environment.apiUrl,
    webHost: '',
    firebaseTenantId: environment.firebaseTenantId,
  },
};

const FALLBACK_MIN_SUPPORTED_VERSION = '0.0.0';

/**
 * Thin wrapper around Firebase Remote Config (fetched on the same Firebase
 * app instance FirebaseAuthService uses - see firebase-app.util.ts).
 *
 * Consulted by BOTH the production and internal builds for:
 *  - `maintenance_mode` (shared banner/page)
 *  - `otp_url` (replaces the old environment.verifyOtpUrl)
 *  - `admin_dashboard_min_supported_version` (stale-tab reload prompt)
 *
 * `environment_directory` (the dev/staging/prod/dev-alt-N backend picker
 * data) is only actually consumed by the internal build's pre-bootstrap gate
 * (src/main.ts) and its "change environment" flow on the login page, but it's
 * exposed here too since Angular DI is available for anything running after
 * bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
  private readonly remoteConfig: RemoteConfig;
  private directory: EnvironmentDirectory = FALLBACK_DIRECTORY;
  private readonly readyPromise: Promise<void>;

  constructor() {
    const app = getOrInitFirebaseApp(environment.firebaseConfig);
    this.remoteConfig = getRemoteConfig(app);
    this.remoteConfig.defaultConfig = {
      [REMOTE_CONFIG_KEYS.environmentDirectory]: JSON.stringify(FALLBACK_DIRECTORY),
      [REMOTE_CONFIG_KEYS.maintenanceMode]: false,
      [REMOTE_CONFIG_KEYS.otpUrl]: environment.verifyOtpUrl,
      [REMOTE_CONFIG_KEYS.minSupportedVersion]: FALLBACK_MIN_SUPPORTED_VERSION,
    };

    this.readyPromise = fetchAndActivate(this.remoteConfig)
      .then(() => this.refreshDirectoryCache())
      .catch((err) => {
        // Network/fetch failures fall back to the bundled defaults above -
        // both builds must keep working if Remote Config is unreachable.
        console.error('[RemoteConfigService] fetchAndActivate failed, using fallback values', err);
      });
  }

  /** Resolves once the initial fetchAndActivate has settled (success or fallback). */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  get environmentDirectory(): EnvironmentDirectory {
    return this.directory;
  }

  get maintenanceMode(): boolean {
    try {
      return getBoolean(this.remoteConfig, REMOTE_CONFIG_KEYS.maintenanceMode);
    } catch {
      return false;
    }
  }

  get otpUrl(): string {
    try {
      return getString(this.remoteConfig, REMOTE_CONFIG_KEYS.otpUrl) || environment.verifyOtpUrl;
    } catch {
      return environment.verifyOtpUrl;
    }
  }

  get minSupportedVersion(): string {
    try {
      return getString(this.remoteConfig, REMOTE_CONFIG_KEYS.minSupportedVersion) || FALLBACK_MIN_SUPPORTED_VERSION;
    } catch {
      return FALLBACK_MIN_SUPPORTED_VERSION;
    }
  }

  private refreshDirectoryCache(): void {
    try {
      const raw = getString(this.remoteConfig, REMOTE_CONFIG_KEYS.environmentDirectory);
      const normalized = raw ? normalizeEnvironmentDirectory(JSON.parse(raw)) : null;
      if (normalized) {
        this.directory = normalized;
      }
    } catch (err) {
      console.error('[RemoteConfigService] failed to parse environment_directory, using fallback', err);
    }
  }
}

/**
 * Simple dot-separated integer version comparison (e.g. "3.11.1" vs "3.12.0")
 * - no semver dependency needed for this. Returns true when `current` is
 * strictly older than `minimum`.
 */
export function isVersionOlderThan(current: string, minimum: string): boolean {
  const currentParts = String(current || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const minimumParts = String(minimum || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const length = Math.max(currentParts.length, minimumParts.length);

  for (let i = 0; i < length; i++) {
    const currentValue = currentParts[i] || 0;
    const minimumValue = minimumParts[i] || 0;
    if (currentValue < minimumValue) return true;
    if (currentValue > minimumValue) return false;
  }
  return false;
}
